import { useLayoutEffect, useRef } from 'react';

/**
 * How an activity row reads as a feed sentence — shared by `FeedPage` (the
 * dedicated Workspace → Feed screen) and the Activities screen's own embedded
 * feed panel, so the two read identically since they are the same data.
 */
export function feedVerb(activity) {
  if (activity.state === 'completed') return { verb: 'completed', tone: 'emerald' };
  if (activity.state === 'cancelled') return { verb: 'cancelled', tone: 'rose' };
  if (activity.is_overdue) return { verb: 'is overdue on', tone: 'rose' };
  return { verb: 'logged', tone: 'sky' };
}

/**
 * The prototype's `#feed` card header has a three-way `All / Mentions / Mine`
 * segmented toggle, but it's decorative there too — nothing in `app.js` wires
 * it to any filtering. `All`/`Mine` get a real answer here (`owner_id`).
 * `Mentions` doesn't: mentions live only on `notes` (§13, polymorphic
 * per-record, not yet built), and `crm.activity` rows carry no mention data
 * at all — so it's kept for layout parity but disabled, same treatment as
 * the composer below.
 */
export function FeedFilterSeg({ value, onChange }) {
  return (
    <div className="seg">
      <button type="button" className={value === 'all' ? 'active' : ''} onClick={() => onChange('all')}>All</button>
      <button
        type="button"
        disabled
        title="There's no mention data to filter on — notes (the only place an @mention can live) aren't built yet."
      >
        Mentions
      </button>
      <button type="button" className={value === 'mine' ? 'active' : ''} onClick={() => onChange('mine')}>Mine</button>
    </div>
  );
}

/**
 * The scroll mechanics behind every chat-style feed on this screen: newest
 * row at the bottom, opens scrolled there, and scrolling to the top pages in
 * the next (older) batch without the view jumping — shared by `FeedPage` (the
 * full-page feed) and `TeamFeedCard` (the Activities screen's embedded
 * panel), which differ only in page size and container height.
 *
 * Returns `{ scrollRef, onScroll }` to spread onto the scroll container, plus
 * `resetScroll` to call whenever a filter changes the underlying rows enough
 * that "open at the bottom" should happen again (a fresh `All`/`Mine` toggle,
 * say — not every render, or new pages loaded normally would keep resetting).
 */
export function useFeedScroll(rows, { hasNextPage, isFetchingNextPage, fetchNextPage }) {
  const scrollRef = useRef(null);
  const prevScrollHeight = useRef(0);
  const hasAutoScrolled = useRef(false);
  /* A plain ref, not `isFetchingNextPage` — a burst of scroll events (several
     fire from one programmatic scrollTop change) can outrun a single React
     render, and reading state that hasn't committed yet let the same page get
     requested more than once. A ref reads its own last write immediately. */
  const fetchingRef = useRef(false);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || el.scrollTop > 80 || !hasNextPage || fetchingRef.current) return;
    fetchingRef.current = true;
    prevScrollHeight.current = el.scrollHeight;
    fetchNextPage().finally(() => { fetchingRef.current = false; });
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prevScrollHeight.current) {
      /* An older page just landed above the fold — hold the same rows in view. */
      el.scrollTop += el.scrollHeight - prevScrollHeight.current;
      prevScrollHeight.current = 0;
    } else if (!hasAutoScrolled.current && rows.length) {
      /* First load — the newest row is the point of a feed, so open at the bottom. */
      el.scrollTop = el.scrollHeight;
      hasAutoScrolled.current = true;
    }
  }, [rows]);

  const resetScroll = () => { hasAutoScrolled.current = false; };

  return { scrollRef, onScroll, resetScroll };
}
